import { supabase } from "../api/supabaseClient";

// ================================================================
//  GenericService — reusable CRUD for any Supabase table
// ================================================================

export const GenericService = {

  // ── Get all rows (paginated) ─────────────────────────────────
  async getAll<T>(
    tableName: string,
    orderBy?: string,
    filters?: Record<string, any>   // optional e.g. { mobile_number: "9539944599" }
  ): Promise<T[]> {
    let allData: any[] = [];
    let from = 0;
    const batchSize = 1000;
    let keepGoing = true;

    while (keepGoing) {
      let query = supabase
        .from(tableName)
        .select("*")
        .range(from, from + batchSize - 1);

      if (orderBy) query = query.order(orderBy, { ascending: true });

      if (filters) {
        Object.entries(filters).forEach(([col, val]) => {
          query = query.eq(col, val);
        });
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);

      allData = allData.concat(data || []);
      if (!data || data.length < batchSize) keepGoing = false;
      else from += batchSize;
    }

    return allData as T[];
  },

  // ── Get single row by ID ─────────────────────────────────────
  async getById<T>(
    tableName: string,
    idColumn: string,
    id: number | string
  ): Promise<T | null> {
    const { data, error } = await supabase
      .from(tableName)
      .select("*")
      .eq(idColumn, id)
      .single();

    if (error) throw new Error(error.message);
    return data as T;
  },

  // ── Insert ───────────────────────────────────────────────────
  async insert(
    tableName: string,
    data: any,
    removeColumns: string[] = []    // columns to strip before insert e.g. ["agency_id"]
  ): Promise<void> {
    const insertData = { ...data };
    removeColumns.forEach((col) => delete insertData[col]);

    const { error } = await supabase.from(tableName).insert(insertData);
    if (error) throw error;
  },

  // ── Update ───────────────────────────────────────────────────
  async update(
    tableName: string,
    idColumn: string,
    data: any
  ): Promise<void> {
    const { error } = await supabase
      .from(tableName)
      .update(data)
      .eq(idColumn, data[idColumn]);

    if (error) throw error;
  },

  // ── Delete ───────────────────────────────────────────────────
  async delete(
    tableName: string,
    idColumn: string,
    id: number | string
  ): Promise<void> {
    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq(idColumn, id);

    if (error) throw error;
  },
};